import React, { useEffect, useState, useMemo, Suspense } from 'react';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { loadingPlansApi } from '@/services/loading-plan-api';
import { BoxPosition, PalletLayout } from '@/types/loading-plan';
import { Loader2, Box } from 'lucide-react';
import { Canvas, useLoader } from '@react-three/fiber';
import { OrbitControls, Environment, useTexture, ContactShadows, PerspectiveCamera, Html, Text, Edges, Line, useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { Box3, Vector3, Mesh, MeshStandardMaterial } from 'three';
import { PALLET_3D_PREVIEW_ENABLED } from '@/constants/featureFlags';

interface Pallet3DViewerProps {
  palletId: string | null;
  type: 'virtual' | 'physical';
  isOpen: boolean;
  onClose: () => void;
  palletNumber: number | string;
  /** Cuando es true renderiza el canvas directamente sin Dialog wrapper */
  embedded?: boolean;
  /** Si se pasa, omite el fetch y usa estos datos directamente */
  initialData?: PalletLayout;
  /** Capa a remarcar: las cajas de esta capa destacan (borde cian/grueso) y las demás se atenúan. */
  highlightedLayerIndex?: number | null;
}

export function Pallet3DViewer({ palletId, type, isOpen, onClose, palletNumber, embedded, initialData, highlightedLayerIndex }: Pallet3DViewerProps) {
  if (!PALLET_3D_PREVIEW_ENABLED) return null;

  const [data, setData] = useState<PalletLayout | null>(initialData ?? null);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setData(initialData);
      return;
    }
    if (palletId && (isOpen || embedded)) {
      loadLayout();
    }
    if (!palletId) setData(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, palletId, initialData]);

  const loadLayout = async () => {
    if (!palletId) return;
    setLoading(true);
    try {
      const result = type === 'virtual'
        ? await loadingPlansApi.getVirtualPalletLayout(palletId)
        : await loadingPlansApi.getPhysicalPalletLayout(palletId);
      console.log('3D Layout Loaded:', result);
      setData(result);
    } catch (error) {
      console.error("Failed to load 3D layout", error);
    } finally {
      setLoading(false);
    }
  };

  const layout = data?.items || [];
  const palletLength = data?.length || 120;
  const palletWidth = data?.width || 80;
  const baseHeight = data?.baseHeight || 14.4;
  const palletModelUrl = data?.palletModelUrl;

  const canvasContent = (
    <div className="relative flex flex-col w-full h-full overflow-hidden bg-slate-50">
      {/* Header Flotante */}
      <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow border border-slate-200 flex items-center gap-3">
          <div className="bg-blue-100 p-1.5 rounded-md">
              <Box className="h-5 w-5 text-blue-600" />
          </div>
          <div>
              <h3 className="font-bold text-sm text-slate-800">Palet #{palletNumber}</h3>
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">{type === 'virtual' ? 'Virtual Plan' : 'Physical Pallet'}</p>
              <p className="text-[10px] text-slate-500 font-mono mt-0.5">{palletLength}x{palletWidth}x{baseHeight}cm</p>
          </div>
      </div>

      {/* Leyenda flotante */}
      <div className="absolute bottom-12 left-4 z-10 bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow border border-slate-200 max-w-[240px] max-h-[50vh] flex flex-col">
          <div className="text-xs font-bold mb-3 text-slate-500 uppercase tracking-wider flex justify-between items-center">
              <span>Contenido</span>
              <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[10px]">{layout.length} cajas</span>
          </div>
          <div className="space-y-2 overflow-y-auto flex-1 custom-scrollbar pr-1">
              {Array.from(new Set(layout.filter(l => !l.isSeparator).map(l => l.productName))).slice(0, 10).map(name => {
                  const item = layout.find(l => l.productName === name && !l.isSeparator);
                  const count = layout.filter(l => l.productName === name && !l.isSeparator).length;
                  return (
                      <div 
                          key={name} 
                          className={`flex items-start gap-2 text-xs group cursor-pointer transition-colors p-1 rounded ${selectedProduct === name ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-slate-50'}`}
                          onMouseEnter={() => setSelectedProduct(name)}
                          onMouseLeave={() => setSelectedProduct(null)}
                      >
                          <div className="w-3 h-3 rounded-sm shadow-sm mt-0.5 flex-shrink-0 border border-black/10" style={{ backgroundColor: item?.color }}></div>
                          <div className="flex-1 min-w-0">
                              <span className={`font-medium block truncate ${selectedProduct === name ? 'text-blue-700' : 'text-slate-700'}`} title={name}>{name}</span>
                              <span className="text-[10px] text-muted-foreground">{count} cajas</span>
                          </div>
                      </div>
                  );
              })}
              {new Set(layout.filter(l => !l.isSeparator).map(l => l.productName)).size > 10 && (
                  <div className="text-[10px] text-center text-muted-foreground italic pt-2 border-t mt-1">
                      + {new Set(layout.filter(l => !l.isSeparator).map(l => l.productName)).size - 10} productos más
                  </div>
              )}
          </div>
      </div>

      {/* Leyenda de prioridad de apilado */}
      <div className="absolute bottom-12 right-4 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow border border-slate-200">
          <div className="text-[10px] font-bold mb-2 text-slate-500 uppercase tracking-wider">Prioridad de apilado</div>
          <div className="space-y-1.5">
              {[
                  { priority: 1, color: "#06b6d4", material: "Plástico rígido", pos: "Base" },
                  { priority: 2, color: "#22c55e", material: "Plástico ligero / Madera", pos: "Medio" },
                  { priority: 3, color: "#a855f7", material: "Cartón", pos: "Arriba" },
                  { priority: 5, color: "#ef4444", material: "Poliestireno (corcho)", pos: "Tope" },
              ].map(s => (
                  <div key={s.priority} className="flex items-center gap-2 text-xs">
                      <div className="w-4 h-3 rounded-sm border-2 bg-white/50 flex-shrink-0" style={{ borderColor: s.color }}></div>
                      <span className="font-semibold text-slate-600 w-3 text-center">{s.priority}</span>
                      <div className="flex-1 min-w-0">
                          <span className="font-medium text-slate-700 block truncate">{s.material}</span>
                          <span className="text-[10px] text-slate-400">{s.pos}</span>
                      </div>
                  </div>
              ))}
          </div>
      </div>

      {/* Controls Instructions */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-black/50 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-xs font-medium pointer-events-none">
          Click izquierdo: Rotar • Click derecho: Mover • Rueda: Zoom
      </div>

      <div className="flex-1 w-full h-full bg-gradient-to-b from-slate-100 to-slate-200 relative">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
              <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
              <span className="text-sm font-medium text-slate-500">Cargando modelo 3D...</span>
          </div>
        ) : (
          <Canvas shadows dpr={[1, 2]} className="w-full h-full">
              <Suspense fallback={null}>
                  <Scene
                      items={layout}
                      palletLength={palletLength}
                      palletWidth={palletWidth}
                      palletHeight={baseHeight}
                      palletModelUrl={palletModelUrl}
                      selectedProduct={selectedProduct}
                      highlightedLayerIndex={highlightedLayerIndex ?? null}
                  />
              </Suspense>
          </Canvas>
        )}
      </div>
    </div>
  );

  if (embedded) {
    if (!palletId && !initialData) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full bg-gradient-to-b from-slate-100 to-slate-200 text-slate-400 select-none">
          <Box className="h-12 w-12 mb-3 opacity-20" />
          <p className="text-sm font-medium">Selecciona un palet</p>
          <p className="text-xs mt-1">para visualizarlo en 3D</p>
        </div>
      );
    }
    return canvasContent;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] w-[90vw] h-[90vh] sm:max-w-[95vw] flex flex-col p-0 overflow-hidden bg-slate-50 border-0 rounded-xl">
        {canvasContent}
      </DialogContent>
    </Dialog>
  );
}

// 3D Model Components
function BoxModel({ box, palletLength, palletWidth, selectedProduct, highlightedLayerIndex }: { box: BoxPosition, palletLength: number, palletWidth: number, selectedProduct: string | null, highlightedLayerIndex: number | null }) {
    if (box.isSeparator) return <SeparatorMesh box={box} palletLength={palletLength} palletWidth={palletWidth} />;
    if (!box.model3DUrl) return <BoxMesh box={box} palletLength={palletLength} palletWidth={palletWidth} selectedProduct={selectedProduct} highlightedLayerIndex={highlightedLayerIndex} />;

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
    const url = box.model3DUrl.startsWith('http') ? box.model3DUrl : `${apiUrl}${box.model3DUrl}`;

    return (
        <Suspense fallback={<BoxMesh box={box} palletLength={palletLength} palletWidth={palletWidth} selectedProduct={selectedProduct} highlightedLayerIndex={highlightedLayerIndex} />}>
            <GLTFBox url={url} box={box} palletLength={palletLength} palletWidth={palletWidth} selectedProduct={selectedProduct} highlightedLayerIndex={highlightedLayerIndex} />
        </Suspense>
    );
}

function GLTFBox({ url, box, palletLength, palletWidth, selectedProduct, highlightedLayerIndex }: { url: string, box: BoxPosition, palletLength: number, palletWidth: number, selectedProduct: string | null, highlightedLayerIndex: number | null }) {
    const { scene } = useGLTF(url);
    const clonedScene = useMemo(() => scene.clone(true), [scene]);

    // Dimensions
    const w = box.length; 
    const d = box.width;  
    const h = box.height;

    // Positioning (Center)
    const x = box.x + w/2 - (palletLength/2);
    const z = box.y + d/2 - (palletWidth/2);
    const y = box.z + h/2;

    // State for transforms
    const [transforms, setTransforms] = useState<{ offset: Vector3, scale: Vector3 } | null>(null);

    // Selection State
    const isSelected = selectedProduct === box.productName;

    // Fix Materials (Reset to Matte)
    useEffect(() => {
        if (!clonedScene) return;
        
        clonedScene.traverse((child) => {
            if ((child as any).isMesh) {
                const mesh = child as THREE.Mesh;
                if (mesh.material) {
                    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                    materials.forEach(mat => {
                        if ((mat as any).isMeshStandardMaterial) {
                            const standardMat = mat as THREE.MeshStandardMaterial;
                            
                            // FORCE MATTE NON-REFLECTIVE MATERIAL
                            standardMat.metalness = 0.0;
                            standardMat.roughness = 1.0;
                            standardMat.envMapIntensity = 0.0;
                            
                            // Reset transparency
                            standardMat.opacity = 1.0;
                            standardMat.transparent = false;
                            standardMat.depthWrite = true;
                            
                            // Strip maps
                            standardMat.roughnessMap = null;
                            standardMat.metalnessMap = null;
                            
                            standardMat.needsUpdate = true;
                        }
                    });
                }
            }
        });
    }, [clonedScene]);

    useEffect(() => {
        if (!clonedScene) return;
        
        // 1. Reset transforms to get true bounding box
        clonedScene.position.set(0, 0, 0);
        clonedScene.rotation.set(0, 0, 0);
        clonedScene.scale.set(1, 1, 1);
        clonedScene.updateMatrixWorld(true);

        // 2. Measure untransformed model
        const bbox = new Box3().setFromObject(clonedScene);
        if (bbox.isEmpty()) return;

        const size = new Vector3();
        bbox.getSize(size);
        const center = new Vector3();
        bbox.getCenter(center);

        // 3. Calculate Scale Factors to fit target dimensions
        // Avoid division by zero
        const scaleX = size.x > 0.001 ? w / size.x : 1;
        const scaleY = size.y > 0.001 ? h / size.y : 1;
        const scaleZ = size.z > 0.001 ? d / size.z : 1;

        // 4. Set Offset to center geometry at (0,0,0) locally
        setTransforms({
            offset: new Vector3(-center.x, -center.y, -center.z),
            scale: new Vector3(scaleX, scaleY, scaleZ)
        });

    }, [clonedScene, w, h, d]);

    if (!transforms) return null;

    return (
        <group position={[x, y, z]}>
            <group scale={transforms.scale}>
                <primitive object={clonedScene} position={transforms.offset} />
            </group>

            {/* Selection Highlight (Red Box Outline) */}
            {isSelected && (
                <mesh position={[0, 0, 0]}>
                    <boxGeometry args={[w, h, d]} />
                    <meshBasicMaterial transparent opacity={0} depthWrite={false} />
                    <Edges 
                        threshold={1} 
                        color="#ef4444" 
                        lineWidth={3}
                        scale={1.02} // Slightly larger to prevent z-fighting
                    />
                </mesh>
            )}
        </group>
    );
}

function PalletModel({ url, length, width, height }: { url: string, length: number, width: number, height: number }) {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
    const fullUrl = url.startsWith('http') ? url : `${apiUrl}${url}`;
    const { scene } = useGLTF(fullUrl);
    
    // Clone scene
    const clonedScene = useMemo(() => scene.clone(true), [scene]);
    
    useEffect(() => {
        if (!clonedScene) return;

        // Reset transforms
        clonedScene.position.set(0, 0, 0);
        clonedScene.rotation.set(0, 0, 0);
        clonedScene.scale.set(1, 1, 1);
        clonedScene.updateMatrixWorld(true);

        const box = new Box3().setFromObject(clonedScene);
        
        if (box.isEmpty()) return;

        const size = new Vector3();
        box.getSize(size);
        const center = new Vector3();
        box.getCenter(center);
        
        // Calculate Bottom Y
        const minY = box.min.y;

        // 1. Calculate Scales first to fit target dimensions
        const scaleX = size.x > 0.01 ? length / size.x : 1;
        const scaleZ = size.z > 0.01 ? width / size.z : 1;
        const scaleY = size.y > 0.01 ? height / size.y : 1;

        // 2. Apply positioning accounting for scale
        // We want Center X/Z at 0, and Bottom Y at 0
        clonedScene.position.x = -center.x * scaleX;
        clonedScene.position.y = -minY * scaleY;
        clonedScene.position.z = -center.z * scaleZ;
        
        console.log('Scaling Pallet Model (Corrected):', { 
            originalHeight: size.y,
            minY,
            targetHeight: height,
            scaleY,
            shiftY: -minY * scaleY
        });

        // 3. Fix Materials (Prevent "burnt" look by removing metalness from wood)
        clonedScene.traverse((child) => {
            if ((child as any).isMesh) {
                const mesh = child as THREE.Mesh;
                if (mesh.material) {
                    // Handle single or array of materials
                    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                    materials.forEach(mat => {
                        if ((mat as any).isMeshStandardMaterial) {
                            const standardMat = mat as THREE.MeshStandardMaterial;
                            
                            // FORCE MATTE WOOD APPEARANCE
                            standardMat.metalness = 0.0;       // Wood is dielectric
                            standardMat.roughness = 1.0;       // Very rough surface
                            standardMat.envMapIntensity = 0.2; // Minimal environment reflections
                            
                            // Remove conflicting maps that might override our settings
                            standardMat.metalnessMap = null;
                            standardMat.roughnessMap = null;
                            
                            standardMat.needsUpdate = true;
                        }
                    });
                }
            }
        });

        clonedScene.scale.set(scaleX, scaleY, scaleZ);
        
    }, [clonedScene, length, width, height]);

    // Position Group at 0 (Floor)
    // The model internal position handles the shifting so it sits on the floor
    return (
        <group position={[0, 0, 0]}>
            <primitive object={clonedScene} />
        </group>
    );
}

function Scene({ items, palletLength, palletWidth, palletHeight, palletModelUrl, selectedProduct, highlightedLayerIndex }: { items: BoxPosition[], palletLength: number, palletWidth: number, palletHeight: number, palletModelUrl?: string | null, selectedProduct: string | null, highlightedLayerIndex: number | null }) {
    // Detect and apply unit scaling (mm to cm)
    const { scaledItems } = useMemo(() => {
        if (items.length === 0) return { scaledItems: [] };
        
        const avgLen = items.reduce((sum, b) => sum + b.length, 0) / items.length;
        // Pallet usually 120cm. If box averages > 150 while pallet < 250, it's likely mm.
        let factor = 1;
        if (avgLen > 150 && palletLength < 250) { 
             factor = 0.1;
        }
        
        const scaled = items.map(b => ({
            ...b,
            length: b.length * factor,
            width: b.width * factor,
            height: b.height * factor,
            x: b.x * factor,
            y: b.y * factor,
            z: b.z * factor
        }));
        
        return { scaledItems: scaled };
    }, [items, palletLength]);

    // Load textures
    const concreteTexture = useTexture("/textures/concrete.png");
    useEffect(() => {
        concreteTexture.wrapS = concreteTexture.wrapT = THREE.RepeatWrapping;
        concreteTexture.repeat.set(8, 8);
        concreteTexture.needsUpdate = true;
    }, [concreteTexture]);

    const wallTexture = useTexture("/textures/wall_concrete.png");
    useEffect(() => {
        wallTexture.wrapS = wallTexture.wrapT = THREE.RepeatWrapping;
        wallTexture.repeat.set(4, 1); 
        wallTexture.needsUpdate = true;
    }, [wallTexture]);

    const logoTexture = useTexture("/images/logo.png");

    // Calcular altura máxima de la carga (API Z is height)
    const maxLoadHeight = scaledItems.length > 0 
        ? Math.max(...scaledItems.map(b => b.z + b.height)) 
        : 0;
    
    // Altura total desde el suelo = altura carga + altura palet
    const totalHeight = maxLoadHeight + palletHeight;

    // Coordenadas para las cotas (Offsets para que no toquen el modelo)
    const offset = 15; // cm de distancia del modelo
    
    const halfL = palletLength / 2;
    const halfW = palletWidth / 2;
    const halfH = palletHeight / 2;

    return (
        <>
            <PerspectiveCamera makeDefault position={[palletLength * 1.5, totalHeight + 100, palletWidth * 1.5]} fov={45} near={1} far={2000} />
            <OrbitControls 
                enablePan={true} 
                enableZoom={true} 
                zoomSpeed={0.5}
                minPolarAngle={0} 
                maxPolarAngle={Math.PI / 2 - 0.05}
                // Limitamos el zoom para mantenerse en la sala (ahora 5.5 metros)
                minDistance={50}
                maxDistance={550}
                dampingFactor={0.05}
                target={[0, totalHeight / 2, 0]} 
            />
            
            {/* Iluminación Estándar de Estudio (Sin HDR para evitar brillos raros) */}
            <ambientLight intensity={0.8} />
            <directionalLight 
                position={[100, 200, 50]} 
                intensity={1.5} 
                castShadow 
                shadow-mapSize={[2048, 2048]} 
                shadow-bias={-0.0005}
                shadow-normalBias={0.02}
            />
            <hemisphereLight intensity={0.6} groundColor="#e2e8f0" color="#ffffff" />
            
            {/* --- ESCENA ESTUDIO INDUSTRIAL --- */}
            
            {/* 1. Iluminación Principal con Sombras Suaves */}
            <ambientLight intensity={0.5} />
            <group position={[100, 200, 100]}>
                <directionalLight 
                    intensity={1.5} 
                    castShadow 
                    shadow-mapSize={[2048, 2048]} 
                    shadow-bias={-0.0005}
                />
            </group>

            {/* 2. Entorno y Reflejos */}
            <Environment files="/3d/warehouse.hdr" blur={0.6} background={false} />
            
            {/* 3. Fondo y Niebla para efecto "infinito" */}
            <color attach="background" args={['#e2e8f0']} />
            <fog attach="fog" args={['#e2e8f0', 400, 1200]} />

            {/* 4. Suelo Infinito con Sombra de Contacto Realista */}
            <group position={[0, -0.5, 0]}>
                {/* Plano de suelo infinito que recibe sombras */}
                <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow position={[0, 0, 0]}>
                    <planeGeometry args={[10000, 10000]} />
                    <meshStandardMaterial 
                        color="#ffffff"
                        map={concreteTexture}
                        roughness={1}
                        metalness={0}
                        envMapIntensity={0.2}
                    />
                </mesh>

                {/* Sombra de contacto acumulativa eliminada por error de WebGL en librería Drei */}
            </group>

            {/* 5. Logo "Flotante" o en Panel al fondo (Opcional, estilo showroom) */}
            <group position={[0, 150, -300]}>
                <mesh>
                    <planeGeometry args={[200, 200 * ((logoTexture.image as HTMLImageElement)?.height / (logoTexture.image as HTMLImageElement)?.width || 1)]} />
                    <meshBasicMaterial 
                        map={logoTexture} 
                        transparent={true}
                        opacity={0.8}
                    />
                </mesh>
            </group>
            {/* Suelo Palet Base */}
            <group position={[0, 0, 0]}>
                {palletModelUrl ? (
                    <ModelErrorBoundary fallback={
                        <mesh position={[0, halfH - 0.05, 0]} receiveShadow castShadow>
                            <boxGeometry args={[palletLength, palletHeight, palletWidth]} />
                            <meshStandardMaterial color="#d4a76a" roughness={0.9} />
                            <Edges color="#8b5a2b" threshold={15} />
                        </mesh>
                    }>
                        <Suspense fallback={
                            <group position={[0, halfH - 0.05, 0]}>
                                 <mesh>
                                    <boxGeometry args={[palletLength, palletHeight, palletWidth]} />
                                    <meshStandardMaterial color="#d4a76a" opacity={0.3} transparent />
                                 </mesh>
                                 <Text position={[0, 20, 0]} fontSize={10} color="black" anchorX="center" anchorY="middle">
                                    Cargando...
                                 </Text>
                            </group>
                        }>
                            <PalletModel url={palletModelUrl} length={palletLength} width={palletWidth} height={palletHeight} />
                        </Suspense>
                    </ModelErrorBoundary>
                ) : (
                    <mesh position={[0, halfH - 0.05, 0]} receiveShadow castShadow>
                        <boxGeometry args={[palletLength, palletHeight, palletWidth]} />
                        <meshStandardMaterial color="#d4a76a" roughness={0.9} />
                        <Edges color="#8b5a2b" threshold={15} />
                    </mesh>
                )}
            </group>

            {/* Cajas */}
            <group position={[0, palletHeight, 0]}>
                {scaledItems.map((box, i) => (
                    <BoxModel
                      key={i}
                      box={box}
                      palletLength={palletLength}
                      palletWidth={palletWidth}
                      selectedProduct={selectedProduct}
                      highlightedLayerIndex={highlightedLayerIndex}
                    />
                ))}
            </group>

            {/* COTAS / MEDIDAS */}
            <group position={[0, 0, 0]}>
                {/* Largo (Eje X) - Delante */}
                <Measurement 
                    start={[-halfL, 0, halfW + offset]} 
                    end={[halfL, 0, halfW + offset]} 
                    label={`${palletLength} cm`} 
                />
                
                {/* Ancho (Eje Z) - Derecha */}
                <Measurement 
                    start={[halfL + offset, 0, -halfW]} 
                    end={[halfL + offset, 0, halfW]} 
                    label={`${palletWidth} cm`} 
                />
                
                {/* Altura Total (Eje Y) - Esquina Derecha-Delante */}
                <Measurement 
                    start={[halfL + offset, 0, halfW + offset]} 
                    end={[halfL + offset, totalHeight, halfW + offset]} 
                    label={`${totalHeight.toFixed(0)} cm`} 
                    color="#ef4444" 
                />
                
                {/* Líneas auxiliares en el suelo para conectar cotas con modelo */}
                <Line points={[[halfL, 0, halfW], [halfL + offset, 0, halfW + offset]]} color="#aaaaaa" dashed opacity={0.3} lineWidth={1} />
            </group>

            {/* Sombras de Contacto de Alta Calidad */}
            <ContactShadows 
                position={[0, 0.01, 0]} 
                opacity={0.6} 
                scale={1000} 
                blur={2} 
                far={100} 
                resolution={1024} 
                color="#000000" 
            />
        </>
    );
}

function SeparatorMesh({ box, palletLength, palletWidth }: { box: BoxPosition, palletLength: number, palletWidth: number }) {
    const w = box.length;
    const d = box.width;
    const h = box.height;
    const x = box.x + w/2 - (palletLength/2);
    const z = box.y + d/2 - (palletWidth/2);
    const y = box.z + h/2;

    return (
        <mesh position={[x, y, z]} receiveShadow>
            <boxGeometry args={[w, h, d]} />
            <meshStandardMaterial color="#8B4513" roughness={0.6} metalness={0} transparent opacity={0.85} />
        </mesh>
    );
}

// Color de borde según prioridad de apilado (fragilidad del material)
const PRIORITY_COLORS: Record<number, string> = {
    1: "#06b6d4",  // cian — PlasticoRigido (más resistente)
    2: "#22c55e",  // verde — PlasticoLigero, Madera
    3: "#a855f7",  // morado — Carton
    5: "#ef4444",  // rojo — Poliestireno (más frágil)
};

function getPriorityColor(priority?: number): string {
    if (!priority) return "#000000";
    return PRIORITY_COLORS[priority] ?? "#000000";
}

function BoxMesh({ box, palletLength, palletWidth, selectedProduct, highlightedLayerIndex }: { box: BoxPosition, palletLength: number, palletWidth: number, selectedProduct: string | null, highlightedLayerIndex: number | null }) {
    const w = box.length;
    const d = box.width;
    const h = box.height;

    // Centrar usando las dimensiones reales del palet
    const x = box.x + w/2 - (palletLength/2);
    const z = box.y + d/2 - (palletWidth/2);
    const y = box.z + h/2;

    const isSelected = selectedProduct === box.productName;
    const isHighlightedLayer = highlightedLayerIndex !== null && (box.layerIndex ?? 0) === highlightedLayerIndex;
    const isDimmed = highlightedLayerIndex !== null && !isHighlightedLayer;

    const priorityColor = getPriorityColor(box.stackingPriority);
    const edgeColor = isSelected ? "#3b82f6" : (isHighlightedLayer ? "#dc2626" : priorityColor);
    const edgeLineWidth = isSelected ? 3 : (isHighlightedLayer ? 3 : 1.5);
    const edgeOpacity = isSelected ? 1 : (isHighlightedLayer ? 1 : 0.6);
    const fillOpacity = isDimmed ? 0.2 : 1;

    return (
        <mesh position={[x, y, z]} castShadow receiveShadow>
            <boxGeometry args={[w, h, d]} />
            <meshStandardMaterial
                color={box.color}
                roughness={0.5}
                metalness={0.1}
                transparent={isDimmed}
                opacity={fillOpacity}
            />
            <Edges
                 threshold={15}
                 color={edgeColor}
                 lineWidth={edgeLineWidth}
                 renderOrder={1000}
                 scale={isSelected || isHighlightedLayer ? 1.01 : 1}
            >
                 <meshBasicMaterial transparent opacity={edgeOpacity} color={edgeColor} />
            </Edges>
        </mesh>
    );
}

// Si el GLB del palet falla (404, CORS, GLB inválido) la excepción se propaga al
// Suspense exterior y deja la escena entera en negro. Aislamos el modelo del palet
// con este boundary para que en caso de fallo se pinte el cubo de madera de respaldo
// y el resto de la escena (cajas, suelo, cotas) siga renderizando.
class ModelErrorBoundary extends React.Component<
  { fallback: React.ReactNode; children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: unknown) {
    console.warn('[Pallet3DViewer] No se pudo cargar el modelo del palet:', error);
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

function Measurement({ start, end, label, color = "#2563eb" }: { start: [number,number,number], end: [number,number,number], label: string, color?: string }) {
    return (
      <group>
        {/* Línea principal */}
        <Line points={[start, end]} color={color} lineWidth={2} />
        
        {/* Topes */}
        <mesh position={start}><sphereGeometry args={[1.5]} /><meshBasicMaterial color={color} /></mesh>
        <mesh position={end}><sphereGeometry args={[1.5]} /><meshBasicMaterial color={color} /></mesh>
        
        {/* Texto */}
        <Text 
          position={[
              (start[0]+end[0])/2, 
              (start[1]+end[1])/2 + 4, 
              (start[2]+end[2])/2
          ]}
          fontSize={8}
          color={color}
          anchorX="center"
          anchorY="bottom"
          outlineWidth={0.5}
          outlineColor="white"
        >
          {label}
        </Text>
      </group>
    )
}
